import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Auth from './components/Auth';
import Payment from './components/Payment';
import Dashboard from './components/Dashboard';
import Header from './components/Header';
import URLInput from './components/URLInput';
import OutputEditor from './components/OutputEditor';
import ActionButtons from './components/ActionButtons';
import Toast from './components/Toast';
import { encryptCode } from './utils/crypto';
import { getSettings } from './utils/storage';
import supabase from './supabase/supabase';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState(null);
  const [url, setUrl] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [encrypted, setEncrypted] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [settings] = useState(getSettings());

  useEffect(() => {
    // Check for existing session
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        setUser(session.user);
        const hasSubscription = await checkSubscription(session.user.id);
        if (!hasSubscription) {
          // Sign them out if they don't have active subscription
          await supabase.auth.signOut();
          setUser(null);
        } else {
          await fetchProfile(session.user.id);
        }
      }
      
      setLoading(false);
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
        const hasSubscription = await checkSubscription(session.user.id);
        if (!hasSubscription) {
          // Sign them out if they don't have active subscription
          await supabase.auth.signOut();
          setUser(null);
          setSubscription(null);
          setProfile(null);
        } else {
          await fetchProfile(session.user.id);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setSubscription(null);
        setProfile(null);
      }
    });

    return () => authSub.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (data && !error) {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const checkSubscription = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      if (data && !error) {
        setSubscription(data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const generateRedirectCode = (inputUrl) => {
    if (!inputUrl.trim()) return '';
    
    const template = settings.redirectTemplate;
    const base64Url = btoa(inputUrl);
    
    return template
      .replace(/URL_ORIGINAL/g, inputUrl)
      .replace(/URL_BASE64/g, base64Url);
  };

  useEffect(() => {
    if (url) {
      const redirectCode = generateRedirectCode(url);
      setPlaintext(redirectCode);
    } else {
      setPlaintext('');
    }
  }, [url, settings.redirectTemplate]);

  const handleEncrypt = async () => {
    if (!url.trim()) {
      showToast('Please enter a URL first', 'error');
      return;
    }

    if (!subscription) {
      showToast('Please subscribe to access this feature', 'error');
      return;
    }

    try {
      const result = encryptCode(plaintext, settings.outputTemplate);
      setEncrypted(result);
      
      await navigator.clipboard.writeText(result);
      showToast('Encrypted code copied to clipboard!');
      
      // Log usage
      await supabase
        .from('usage_logs')
        .insert({
          user_id: user.id,
          url: url,
          encrypted_length: result.length
        });
        
    } catch (error) {
      console.error(error);
      showToast('Encryption failed: ' + error.message, 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Auth showToast={showToast} />;
  }

  if (!subscription) {
    return <Payment user={user} showToast={showToast} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          <Header user={user} subscription={subscription} profile={profile} />
          
          <div className="space-y-6 bg-slate-800/30 p-6 rounded-2xl border border-slate-700/30 backdrop-blur-sm">
            <URLInput value={url} onChange={setUrl} />
            
            <ActionButtons onEncrypt={handleEncrypt} />
            
            <OutputEditor value={encrypted} onChange={setEncrypted} />
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ show: false, message: '', type: 'success' })}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;